import { EventNames, isServerRendering } from '@farmasiet/frontendcore-reactcomponents';
import {
    DynamicHeightMessage,
    GenericIFrameMessage,
    GetEcomDataResponseMessage,
    IFrameMessageCallbackType,
    IFrameMessageSender,
    MessageType,
    ModalClosedMessage,
    ModifyModalSwipeableOverlayMessage,
    NavigatedMessage,
    PhilModalVariant,
    QuantityChangeReasonMessage,
    RedirectMessage,
    RedirectTarget,
    RedirectToErrorPageMessage,
    RestoreScrollPositionMessage,
    RxRemovedFromCartMessage,
    ScrollToTopMessage,
    SetDateMessage,
    SetErrorMessage,
    ShowCooledProductConflictModalMessage,
    ShowDownloadPrescriptionHistoryButtonMessage,
    ShowModalMessage,
    StoreScrollPositionMessage,
    TriggerOpenDeliveryModalMessage,
    useMessageReceivers
} from '@farmasiet/iframe-messages';
import { Action, BrowserHistory, createBrowserHistory, Update } from 'history';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import urlJoin from 'url-join';

import { getTranslation, keys } from '../../../Resources/Translations';
import { PhilAlert } from '../../Blocks/PhilAlert/PhilAlert';
import { IframeBreadcrumbs } from '../../Partials/Breadcrumbs/IframeBreadcrumbs';
import { CooledProductsConflictEvent } from '../../ReactComponents/CooledProductsConflict/CooledProductsConflictEvent';
import { CooledProductsConflictItem } from '../../ReactComponents/CooledProductsConflict/CooledProductsConflictItem';
import { DownloadPrescriptionHistory } from '../../ReactComponents/Phil/DownloadPrescriptionHistory/DownloadPrescriptionHistory';
import { ErrorMessageBar } from '../../ReactComponents/Phil/ErrorMessageBar/ErrorMessageBar';
import { PhilModal } from '../../ReactComponents/Phil/PhilModal/PhilModal';
import { RelativeURL } from '../../ReactComponents/RelativeURL';
import { smoothScrollTo } from '../../ReactComponents/SmoothScrollTo';
import { EmbeddedIframeProps } from './EmbeddedIframeProps.csharp';
import { RxMaintenancePlaceholder } from './RxMaintenancePlaceholder/RxMaintenancePlaceholder';

export const EmbeddedIframe = (props: EmbeddedIframeProps) => {
    const redirectsCountParamName = 'redirectsCount';
    const maxRedirectsAmount = 2;
    const offlinePrescriptionIdParam = 'offlinePrescriptionId';
    const offlinePrescriptionPath = '/offline-prescription';

    // eslint-disable-next-line testing-library/render-result-naming-convention
    const isSsr = isServerRendering();
    const [iframeUrl, setIframeUrl] = useState<string | undefined>();
    const [height, setHeight] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showDownloadHistoryButton, setShowDownloadHistoryButton] = useState(false);
    const [modalProps, setModalProps] = useState<ShowModalMessage>(new ShowModalMessage(PhilModalVariant.Center, ''));
    const [errorMessageBarText, setErrorMessageBarText] = useState<string | null>(null);
    const historyRef = useRef<BrowserHistory | null>(null);
    const hideAlertRibbonTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const messageSender = IFrameMessageSender.create(props.iframeUrl, iframeRef);
    const scrollPositions = useRef<{ [key: string]: number }>({});
    const requestedScrollPosition = useRef<number | undefined>(undefined);

    const version = useRef(new Date().getTime().toString());

    useEffect(() => {
        window.addEventListener('blur', handleBlur);
        window.addEventListener(EventNames.RxRemovedFromCart, handleRxRemovedFromCart);
        window.addEventListener(EventNames.RxLoginRequest, handleRxLoginRequest);

        const url = new URL(props.iframeUrl);
        url.searchParams.set('v', version.current);

        const offlinePrescriptionId = getOfflinePrescriptionId();
        if (offlinePrescriptionId) {
            url.hash = urlJoin(offlinePrescriptionPath, offlinePrescriptionId);
        }

        const params = new URLSearchParams(window.location.search);
        if (params.has('path')) {
            const pathUrl = new RelativeURL(decodeURI(params.get('path')!));
            url.pathname = urlJoin(url.pathname, pathUrl.pathname);
            pathUrl.searchParams.forEach((value, key) => url.searchParams.set(key, value));
        }

        setIframeUrl(url.href);

        historyRef.current = createBrowserHistory();
        const cleanupHistoryListener = historyRef.current.listen(onHistoryUpdate);

        hideAlertRibbonTimeout.current = setTimeout(() => {
            window.dispatchEvent(new CustomEvent(EventNames.HideAlertRibbon));
        }, 0);

        return () => {
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener(EventNames.RxRemovedFromCart, handleRxRemovedFromCart);
            window.removeEventListener(EventNames.RxLoginRequest, handleRxLoginRequest);
            cleanupHistoryListener();
            if (hideAlertRibbonTimeout.current) {
                clearTimeout(hideAlertRibbonTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        if (requestedScrollPosition.current) {
            scrollTo({ top: requestedScrollPosition.current! });
            requestedScrollPosition.current = undefined;
        }
    }, [height]);

    const handleBlur = () => {
        window.setTimeout(() => {
            if (document.activeElement == iframeRef.current) {
                window.dispatchEvent(new CustomEvent(EventNames.IframeFocused));
            }
        });
    };

    const handleRxRemovedFromCart = () => {
        messageSender.sendMessage(new RxRemovedFromCartMessage());
    };

    const handleRxLoginRequest = (event: Event) => {
        event.preventDefault();
        redirectToPhilLogin();
    };

    const onHistoryUpdate = (update: Update) => {
        if (update.action !== Action.Pop) {
            return;
        }

        const params = new URLSearchParams(update.location.search);
        messageSender.sendMessage(new NavigatedMessage(params.get('path') ?? '/'));
    };

    const redirectToPhilLogin = () => {
        const currentPageUrl = new URL(window.location.href);
        currentPageUrl.searchParams.set(redirectsCountParamName, `${getRedirectsCount() + 1}`);

        let redirectUrl: string;
        if (props.isLoggedIn) {
            redirectUrl = generateIdentityAuthorizeUrl(currentPageUrl.href);
        } else {
            redirectUrl = currentPageUrl.href;
        }

        const url = new URL(props.philOAuthUrl);
        url.searchParams.set('returnUrl', redirectUrl);

        window.location.href = url.href;
    };

    const redirectToPhilAndEcomLogin = () => {
        const currentPageUrl = new URL(window.location.href);
        currentPageUrl.searchParams.set(redirectsCountParamName, `${getRedirectsCount() + 1}`);

        const redirectUrl = new URL(props.ecomLoginUrl, window.location.origin);
        redirectUrl.searchParams.set('redirect_uri', generateIdentityAuthorizeUrl(currentPageUrl.href));

        const url = new URL(props.philOAuthUrl);
        url.searchParams.set('returnUrl', redirectUrl.href);

        window.location.href = url.href;
    };

    const redirectoToEcomLogin = (purchaseRequestKey?: string) => {
        const currentPageUrl = new URL(window.location.href);
        if (purchaseRequestKey) {
            const pathUrl = new RelativeURL(currentPageUrl.searchParams.get('path')!);
            pathUrl.searchParams.set('addToCartAction', purchaseRequestKey);
            currentPageUrl.searchParams.set('path', pathUrl.href);
        }

        const url = new URL(props.ecomLoginUrl, window.location.origin);
        url.searchParams.set('redirect_uri', generateIdentityAuthorizeUrl(currentPageUrl.href));

        window.location.href = url.href;
    };

    const generateIdentityAuthorizeUrl = (redirectUrl: string) => {
        const identityServerUrl = new URL('/connect/authorize', props.identityServerAuthorityUrl);

        identityServerUrl.searchParams.set('client_id', 'Phil');
        identityServerUrl.searchParams.set('scope', 'openid RxApi');
        identityServerUrl.searchParams.set('response_type', 'code');
        identityServerUrl.searchParams.set('state', redirectUrl);

        const encodedIdentityServerUrl = encodeURI(`${identityServerUrl.href}&redirect_uri=`);
        return `${encodedIdentityServerUrl}${props.philLoginUrl}`;
    };

    const getOfflinePrescriptionId = () => {
        const query = new URLSearchParams(window?.location.search || '');
        return query.get(offlinePrescriptionIdParam);
    };

    const getRedirectsCount = () => {
        if (isSsr) return 0;
        const query = new URLSearchParams(window?.location.search || '');
        const redirectsCount = query.get(redirectsCountParamName);
        return redirectsCount ? parseInt(redirectsCount) : 0;
    };

    const getShouldBeLoggedFromQueryParams = () => {
        if (isSsr) return false;

        if (getRedirectsCount() > maxRedirectsAmount) {
            window.history.pushState({}, document.title, window.location.pathname);
            return true;
        }
        return false;
    };

    function handleRedirect(target?: RedirectTarget | string, redirectUrl?: string, purchaseRequestKey?: string) {
        switch (target) {
            case RedirectTarget.PHIL_LOGIN:
                if (!getShouldBeLoggedFromQueryParams()) {
                    redirectToPhilLogin();
                }
                break;
            case RedirectTarget.USER_TRIGGERED_PHIL_LOGIN:
                redirectToPhilLogin();
                break;
            case RedirectTarget.SUPPORT_PAGE:
                window.location.href = props.customerSupportUrl;
                break;
            case RedirectTarget.PRESCRIPTION_LIST:
                window.location.href = props.prescriptionListUrl;
                break;
            case RedirectTarget.ECOM_LOGIN_THEN_ADD_PRODUCT_TO_CART:
            case RedirectTarget.ECOM_LOGIN:
                redirectoToEcomLogin(purchaseRequestKey);
                break;
            case RedirectTarget.ECOM_AND_PHIL_LOGIN:
                redirectToPhilAndEcomLogin();
                break;
            default:
                hideModal();
                window.location.href = target as string;
                break;
        }
    }

    function handleRedirectToErrorPage(code: number): void {
        const currentPageUrl = RelativeURL.fromAbsolute(window.location.href);
        currentPageUrl.searchParams.delete('path');

        const url = new URL(window.location.origin);
        url.pathname = urlJoin('error', `${code}`);
        url.searchParams.set('url', currentPageUrl.href);

        window.location.href = url.href;
    }

    function scrollToTop(isSmooth: boolean) {
        if (isSmooth) smoothScrollTo(0);
        else scrollTo({ top: 0 });
    }

    function storeScrollPosition(key: string) {
        scrollPositions.current[key] = window.scrollY;
    }

    function restoreScrollPosition(key: string) {
        if (key in scrollPositions.current) {
            requestedScrollPosition.current = scrollPositions.current[key];
            delete scrollPositions.current[key];
        }
    }

    const updateCart = () => {
        window.dispatchEvent(new CustomEvent(EventNames.RequestForUpdatingCart));
    };

    const onConflictedProductsKept = () => {
        updateCart();
        handleRxRemovedFromCart();
    };

    const hideModal = () => {
        setShowModal(false);
        messageSender.sendMessage(new ModalClosedMessage());
    };

    const closeErrorMessageBar = () => {
        setErrorMessageBarText(null);
    };

    function getStyles() {
        if (height) {
            return { height: height, maxHeight: 'initial', minHeight: '70vh' };
        }
    }

    useMessageReceivers(props.philUrl, {
        [MessageType.REDIRECT]: (message: RedirectMessage) => handleRedirect(message.target, message.location, message.purchaseRequestKey),
        [MessageType.REDIRECT_TO_ERROR_PAGE]: (message: RedirectToErrorPageMessage) => handleRedirectToErrorPage(message.code),
        [MessageType.SET_HEIGHT]: (message: DynamicHeightMessage) => setHeight(message.size),
        [MessageType.SCROLL_TO_TOP]: (message: ScrollToTopMessage) => scrollToTop(message.useSmoothScroll),
        [MessageType.STORE_SCROLL_POSITION]: (message: StoreScrollPositionMessage) => storeScrollPosition(message.key),
        [MessageType.RESTORE_SCROLL_POSITION]: (message: RestoreScrollPositionMessage) => restoreScrollPosition(message.key),
        [MessageType.UPDATE_CART]: () => updateCart(),
        [MessageType.SHOW_MODAL]: (message: ShowModalMessage) => {
            setModalProps(message);
            setShowModal(true);
        },
        [MessageType.NAVIGATED]: (message: NavigatedMessage) => {
            updateUrl(message.path);
            setErrorMessageBarText(null);
            hideModal();
        },
        [MessageType.QUANTITY_CHANGE_REASON]: (message: QuantityChangeReasonMessage) => messageSender.sendMessage(message),
        [MessageType.SET_ERROR]: (message: SetErrorMessage) => {
            const errorMsg = message.error ? message.error : message.error === null ? null : getTranslation(keys['errorMessageBar.defaultMessage']);
            setErrorMessageBarText(errorMsg);
        },
        [MessageType.ESCAPE_KEY_PRESSED]: () => {
            hideModal();
        },
        [MessageType.MODIFY_MODAL_SWIPEABLE_OVERLAY]: (message: ModifyModalSwipeableOverlayMessage) => {
            setModalProps((modalProps) => ({
                ...modalProps,
                shorterSwipeableOverlay: !message.makeFullWidth
            }));
        },
        [MessageType.SHOW_DOWNLOAD_PRESCRIPTION_HISTORY_BUTTON]: (message: ShowDownloadPrescriptionHistoryButtonMessage) => {
            setShowDownloadHistoryButton(message.showButton);
        },
        [MessageType.SET_DATE]: (message: SetDateMessage) => {
            messageSender.sendMessage(message);
            setShowModal(false);
        },
        [MessageType.DOWNLOAD_PRESCRIPTION_HISTORY]: () => handleDownloadPrescriptionHistory(),
        [MessageType.TRIGGER_OPEN_DELIVERY_MODAL]: (message: TriggerOpenDeliveryModalMessage) => {
            window.dispatchEvent(new CustomEvent(EventNames.OpenDeliveryModal, { detail: { deliveryMethods: message.deliveryMethods } }));
        },
        [MessageType.GET_ECOM_DATA_REQUEST]: () => messageSender.sendMessage(new GetEcomDataResponseMessage(props.rxData)),
        [MessageType.SHOW_COOLED_PRODUCT_CONFLICT_MODAL]: (message: ShowCooledProductConflictModalMessage) =>
            showCooledProductConflictModal(message.productCode, message.isCooled)
    });

    const sendMessageToIFrame = (value: IFrameMessageCallbackType) => {
        messageSender.sendMessage(new GenericIFrameMessage(value));
    };

    const updateUrl = (path: string) => {
        const url = new URL(window.location.href);
        url.searchParams.delete(redirectsCountParamName);
        const targetPath = path === '/' ? null : path;
        if (targetPath === url.searchParams.get('path')) {
            return;
        }

        if (path === '/') {
            url.searchParams.delete('path');
        } else {
            url.searchParams.set('path', path);
        }

        historyRef.current!.push(url.href);
    };

    const handleDownloadPrescriptionHistory = () => {
        window.open(props.prescriptionHistoryExportUrl, '_blank');
    };

    const showCooledProductConflictModal = (productCode: string, isCooled: boolean) => {
        updateCart();
        window.dispatchEvent(
            CooledProductsConflictEvent.create({
                product: {
                    code: productCode,
                    nameUrl: props.rxItemNameUrl + productCode,
                    imageUrl: props.rxItemImageUrl + productCode,
                    name: props.rxItemNamePlaceholder,
                    image: props.rxItemImagePlaceholder.picture,
                    isRx: true,
                    isCooled: isCooled
                } as CooledProductsConflictItem,

                onThisKept: updateCart,
                onConflictedKept: onConflictedProductsKept
            })
        );
    };

    return (
        <>
            <PhilAlert {...props.philAlert} />
            <IframeBreadcrumbs {...props.breadcrumbs} currentPageUrl={props.currentPageUrl} messageSender={messageSender} />
            {(props.rxMaintenance?.showMessage && <RxMaintenancePlaceholder header={props.rxMaintenance.header} message={props.rxMaintenance.message} />) || (
                <>
                    <div className="EmbeddedIframe__Wrapper" style={getStyles()}>
                        <iframe
                            title={props.title}
                            src={iframeUrl}
                            sandbox="allow-scripts allow-same-origin allow-popups allow-downloads"
                            scrolling="no"
                            ref={iframeRef}
                        />
                    </div>
                    {showDownloadHistoryButton && <DownloadPrescriptionHistory onClick={handleDownloadPrescriptionHistory} />}
                    <PhilModal
                        isVisible={showModal}
                        hideModal={hideModal}
                        customerServiceLabel={props.customerServiceLabel}
                        customerServicePhoneNumber={props.customerServiceNumber}
                        customerServiceUrl={props.customerSupportUrl}
                        showModalProps={modalProps}
                        philUrl={props.philUrl}
                        version={version.current}
                        rxData={props.rxData}
                        callback={sendMessageToIFrame}
                    />
                    <ErrorMessageBar message={errorMessageBarText} onClose={closeErrorMessageBar} />
                </>
            )}
        </>
    );
};
